import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  loadStore,
  searchCandidates,
  searchJobs,
  getCandidate,
  getJob,
  getEmployer,
  createJob,
  suggestMatches,
  relatedCandidates,
  type CandidateFilters,
  type SeedCandidate,
  type SeedJob,
} from './store';

const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));

export function useSearchCandidates(filters: CandidateFilters) {
  return useQuery({
    queryKey: ['mock', 'candidates', filters],
    queryFn: async () => {
      await delay();
      return searchCandidates(filters);
    },
  });
}

export function useSearchJobs(filters: CandidateFilters) {
  return useQuery({
    queryKey: ['mock', 'jobs', filters],
    queryFn: async () => {
      await delay();
      return searchJobs(filters);
    },
  });
}

export function useCandidateDetail(slug: string) {
  return useQuery({
    queryKey: ['mock', 'candidate', slug],
    queryFn: async () => {
      await delay(200);
      const c = getCandidate(slug);
      if (!c) throw new Error('not-found');
      return c;
    },
    enabled: !!slug,
  });
}

export function useJobDetail(id: string) {
  return useQuery({
    queryKey: ['mock', 'job', id],
    queryFn: async () => {
      await delay(200);
      const j = getJob(id);
      if (!j) throw new Error('not-found');
      return j;
    },
    enabled: !!id,
  });
}

export function useEmployer(id: string) {
  return useQuery({
    queryKey: ['mock', 'employer', id],
    queryFn: () => getEmployer(id),
    enabled: !!id,
  });
}

export function useRelatedCandidates(slug: string) {
  return useQuery({
    queryKey: ['mock', 'related', slug],
    queryFn: () => relatedCandidates(slug),
    enabled: !!slug,
  });
}

export function useSuggestedJobs(candidateSlug: string) {
  return useQuery({
    queryKey: ['mock', 'matches', candidateSlug],
    queryFn: async () => {
      await delay(200);
      return suggestMatches(candidateSlug);
    },
    enabled: !!candidateSlug,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<SeedJob> & { employerName: string }) => {
      const job = createJob(input);
      return Promise.resolve(job);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['mock', 'jobs'] });
    },
  });
}

export function useAllData() {
  return useQuery({
    queryKey: ['mock', 'store'],
    queryFn: () => loadStore(),
  });
}

export type { SeedCandidate, SeedJob };
